import React, { useState, useEffect } from "react";
import { useTranslation } from "../../../i18n/LanguageContext";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCcVisa,
  faCcMastercard,
  faCcAmex,
  faCcDiscover,
} from "@fortawesome/free-brands-svg-icons";
import { faCreditCard, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import StripePaymentAPI from "../services/StripePaymentAPI";
import { useAccountSetup } from "../context/AccountSetupContext";
import toast from "react-hot-toast";
import ConfirmationModal from "../common/ConfirmationModal";
import "../css/Common.css";
import "../css/Payments.css";

// =============================================================================
// Stripe publishable key — REACT_APP_STRIPE_PUBLISHABLE_KEY
// =============================================================================
// CRA inlines `process.env.REACT_APP_*` at build time, so any misconfiguration
// (missing, blank, or a copied-from-.env.example placeholder) silently ships
// to the bundle and only surfaces as an opaque "Invalid API Key" toast when
// the user tries to add a card. The guard below validates the key shape up
// front, refuses to call loadStripe with garbage, and exposes a flag the form
// can use to render an admin-facing error instead of a broken Stripe Element.
const _stripeKey = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "").trim();

// Real Stripe publishable keys look like: pk_test_<24+ alnum> or pk_live_<24+ alnum>.
// Anything else (placeholder text from .env.example, blank, secret key, etc.)
// is rejected so we fail loud rather than make a bad request to Stripe.
const STRIPE_PK_PATTERN = /^pk_(test|live)_[A-Za-z0-9]{24,}$/;
const stripeKeyValid = STRIPE_PK_PATTERN.test(_stripeKey);

if (!stripeKeyValid) {
  // Loud console error so this is obvious in DevTools / Sentry without leaking
  // the (already public) key value.
  // eslint-disable-next-line no-console
  console.error(
    "[Stripe] REACT_APP_STRIPE_PUBLISHABLE_KEY is missing or malformed. " +
      "Payment methods are disabled. Set a valid pk_test_/pk_live_ key in the " +
      "build environment and redeploy.",
  );
}

const stripePromise = stripeKeyValid ? loadStripe(_stripeKey) : null;

// Function to get Stripe element options based on theme
const getCardElementOptions = (isDarkTheme) => ({
  style: {
    base: {
      color: isDarkTheme ? "#f3f4f6" : "#1f2937",
      fontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: isDarkTheme ? "#9ca3af" : "#6b7280",
      },
    },
    invalid: {
      color: "#ef4444",
      iconColor: "#ef4444",
    },
  },
});

/**
 * Component for adding a new payment method
 */
const AddPaymentMethodForm = ({
  onSuccess,
  onCancel,
  userEmail,
  userName,
  accountId,
  existingPaymentMethods = [],
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState(userName || "");
  // Auto-check the "Set as default" box whenever there is no existing default
  // payment method on file — covers both the truly-empty case and the
  // stale/orphan case where a non-default card exists but the user is
  // effectively choosing their default for the first time.
  // Lock the checkbox only when there are zero cards on file (must be default).
  const isFirstCard = existingPaymentMethods.length === 0;
  const hasExistingDefault = existingPaymentMethods.some((pm) => pm.isDefault);
  const [setAsDefault, setSetAsDefault] = useState(
    isFirstCard || !hasExistingDefault,
  );

  // Detect current theme
  const isDarkTheme =
    document.documentElement.getAttribute("data-theme") === "dark";
  const cardElementOptions = getCardElementOptions(isDarkTheme);
  const cardNumberElementOptions = {
    ...cardElementOptions,
    showIcon: true,
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      // Create setup intent
      const { clientSecret, customerId } =
        await StripePaymentAPI.createSetupIntent(userEmail, cardholderName);

      // Confirm card setup
      const { error, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: {
              name: cardholderName,
            },
          },
        },
      );

      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }

      // Save payment method to backend
      await StripePaymentAPI.savePaymentMethod(
        setupIntent.payment_method,
        customerId,
        accountId,
        setAsDefault,
      );

      toast.success(t("paymentMethods.toast.added"));
      onSuccess();
    } catch (error) {
      console.error("Error adding payment method:", error);
      toast.error(t("paymentMethods.toast.addFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="common-form-field">
        <label>{t("paymentMethods.form.cardholderName")}</label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder={t("paymentMethods.form.namePlaceholder")}
          required
        />
      </div>

      <div className="common-form-field">
        <label>{t("paymentMethods.form.cardNumber")}</label>
        <div className="stripe-card-element">
          <CardNumberElement options={cardNumberElementOptions} />
        </div>
      </div>

      <div className="card-details-row">
        <div className="common-form-field">
          <label>{t("paymentMethods.form.expDate")}</label>
          <div className="stripe-card-element">
            <CardExpiryElement options={cardElementOptions} />
          </div>
        </div>

        <div className="common-form-field">
          <label>{t("paymentMethods.form.cvv")}</label>
          <div className="stripe-card-element">
            <CardCvcElement options={cardElementOptions} />
          </div>
        </div>

        <div className="common-form-field">
          <label>{t("paymentMethods.form.zipCode")}</label>
          <input
            type="text"
            placeholder="12345"
            maxLength="5"
            pattern="[0-9]{5}"
          />
        </div>
      </div>

      <div className="common-form-field">
        <div className="set-default-checkbox">
          <input
            type="checkbox"
            checked={setAsDefault}
            onChange={(e) => setSetAsDefault(e.target.checked)}
            disabled={isFirstCard}
          />
          <span>
            {t("paymentMethods.form.setDefault")}
            {isFirstCard && ` ${t("paymentMethods.form.setDefaultRequired")}`}
          </span>
        </div>
      </div>

      <div className="common-form-actions">
        <button
          type="submit"
          className="payment-form-submit-btn"
          disabled={!stripe || loading}
        >
          {loading
            ? t("paymentMethods.form.adding")
            : t("paymentMethods.form.continue")}
        </button>
        <button
          type="button"
          className="payment-form-cancel-btn"
          onClick={onCancel}
          disabled={loading}
        >
          {t("paymentMethods.form.cancel")}
        </button>
      </div>
    </form>
  );
};

const PaymentMethods = ({
  onBackToSetup,
  autoOpenAddForm,
  onAddFormClosed,
  onCompleteSubscription,
  currentSubscription,
}) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [userEmail, setUserEmail] = useState(""); // Get from context
  const [userName, setUserName] = useState(""); // Get from context
  const [accountId, setAccountId] = useState(1); // Get from context
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState(null);
  const { refreshSetupProgress, setupProgress } = useAccountSetup();
  const { t } = useTranslation();

  const hasDefaultPaymentMethod = paymentMethods.some((pm) => pm.isDefault);
  const shouldShowCompleteSubscriptionCta =
    Boolean(onCompleteSubscription) &&
    setupProgress?.hasCheckoutDraft &&
    !setupProgress?.hasSubscription &&
    hasDefaultPaymentMethod;

  const hasPendingActivation =
    setupProgress?.hasCheckoutDraft && !setupProgress?.hasSubscription;

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  // Auto-open the Add New Card form when the prop flips to true (e.g. after
  // the user selects a paid plan and no card is on file yet).
  useEffect(() => {
    if (autoOpenAddForm && !loading && !showAddForm) {
      setShowAddForm(true);
    }
  }, [autoOpenAddForm]);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const methods = await StripePaymentAPI.getPaymentMethods();

      // If there's only one payment method and it's not set as default, make it default
      if (methods.length === 1 && !methods[0].isDefault) {
        try {
          await StripePaymentAPI.setDefaultPaymentMethod(
            methods[0].paymentMethodId,
          );
          // Reload to get updated default status
          const updatedMethods = await StripePaymentAPI.getPaymentMethods();
          setPaymentMethods(updatedMethods);
          // Only auto-open if no cards on file
          if (autoOpenAddForm && updatedMethods.length === 0) {
            setShowAddForm(true);
          }
        } catch (error) {
          console.error("Error auto-setting default payment method:", error);
          // Still set the methods even if auto-default fails
          setPaymentMethods(methods);
          if (autoOpenAddForm && methods.length === 0) {
            setShowAddForm(true);
          }
        }
      } else {
        setPaymentMethods(methods);
        // Only auto-open if no cards on file
        if (autoOpenAddForm && methods.length === 0) {
          setShowAddForm(true);
        }
      }
    } catch (error) {
      console.error("Error loading payment methods:", error);
      toast.error(t("paymentMethods.toast.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const isRemovalBlocked = () => {
    if (paymentMethods.length > 1) return false;
    const isActive = currentSubscription?.status?.toLowerCase() === "active";
    const isFullSubscription = !currentSubscription?.isTrial;
    const hasAutoRenew = currentSubscription?.autoRenew;
    return isActive && isFullSubscription && hasAutoRenew;
  };

  const handleDeletePaymentMethod = (paymentMethodId) => {
    setPaymentMethodToDelete(paymentMethodId);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePaymentMethod = async () => {
    if (!paymentMethodToDelete) return;

    try {
      const deletedMethod = paymentMethods.find(
        (pm) => pm.paymentMethodId === paymentMethodToDelete,
      );
      const wasDefault = deletedMethod?.isDefault;

      await StripePaymentAPI.deletePaymentMethod(paymentMethodToDelete);
      toast.success(t("paymentMethods.toast.deleted"));

      // If we deleted the default, auto-set the last remaining card as default
      if (wasDefault) {
        const remaining = paymentMethods.filter(
          (pm) => pm.paymentMethodId !== paymentMethodToDelete,
        );
        if (remaining.length > 0) {
          const newDefault = remaining[remaining.length - 1];
          try {
            await StripePaymentAPI.setDefaultPaymentMethod(
              newDefault.paymentMethodId,
            );
            toast.success(
              t("paymentMethods.toast.defaultSet")
                .replace(
                  "{brand}",
                  newDefault.cardBrand?.toUpperCase() || "Card",
                )
                .replace("{last4}", newDefault.cardLast4),
            );
          } catch (err) {
            console.error("Error auto-setting new default:", err);
          }
        }
      }

      await loadPaymentMethods();
      // Refresh account setup progress
      if (refreshSetupProgress) {
        await refreshSetupProgress();
      }
    } catch (error) {
      console.error("Error deleting payment method:", error);
      toast.error(t("paymentMethods.toast.deleteFailed"));
    } finally {
      setShowDeleteConfirm(false);
      setPaymentMethodToDelete(null);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      await StripePaymentAPI.setDefaultPaymentMethod(paymentMethodId);
      toast.success(t("paymentMethods.toast.defaultUpdated"));
      loadPaymentMethods();
    } catch (error) {
      console.error("Error setting default payment method:", error);
      toast.error(t("paymentMethods.toast.defaultFailed"));
    }
  };

  const getCardBrandIcon = (brand) => {
    const brandLower = brand?.toLowerCase();
    switch (brandLower) {
      case "visa":
        return <FontAwesomeIcon icon={faCcVisa} />;
      case "mastercard":
        return <FontAwesomeIcon icon={faCcMastercard} />;
      case "amex":
      case "american express":
        return <FontAwesomeIcon icon={faCcAmex} />;
      case "discover":
        return <FontAwesomeIcon icon={faCcDiscover} />;
      default:
        return <FontAwesomeIcon icon={faCreditCard} />;
    }
  };

  if (loading) {
    return <div className="common-loading">{t("paymentMethods.loading")}</div>;
  }

  return (
    <>
      <div className="common-form-group">
        {!showAddForm && (
          <div className="payment-methods-header">
            <h4>{t("paymentMethods.savedTitle")}</h4>
            <button
              className="btn btn-primary indented-below-h4"
              onClick={() => setShowAddForm(true)}
            >
              {t("paymentMethods.addNewCard")}
            </button>
          </div>
        )}

        {showAddForm && (
          <div className="add-payment-form-container indented-below-h4">
            <h5>{t("paymentMethods.addTitle")}</h5>
            {!stripePromise ? (
              <div
                className="common-message error"
                style={{ marginBottom: "var(--spacing-md)" }}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>{t("paymentMethods.stripeUnavailable")}</span>
              </div>
            ) : (
              <Elements stripe={stripePromise}>
                <AddPaymentMethodForm
                  onSuccess={async () => {
                    setShowAddForm(false);
                    await loadPaymentMethods();
                    // Refresh account setup progress
                    if (refreshSetupProgress) {
                      await refreshSetupProgress();
                    }
                  }}
                  onCancel={() => {
                    setShowAddForm(false);
                    if (onAddFormClosed) onAddFormClosed();
                  }}
                  userEmail={userEmail}
                  userName={userName}
                  accountId={accountId}
                  existingPaymentMethods={paymentMethods}
                />
              </Elements>
            )}
          </div>
        )}

        {!showAddForm &&
          paymentMethods.length === 0 &&
          hasPendingActivation && (
            <div
              className="common-message info indented-below-h4"
              style={{ marginBottom: "var(--spacing-md)" }}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
              <span>{t("paymentMethods.activatePlanInfo")}</span>
            </div>
          )}

        {!showAddForm && paymentMethods.length === 0 && (
          <div className="common-no-data indented-below-h4">
            <p>{t("paymentMethods.noMethodsTitle")}</p>
            <p>{t("paymentMethods.noMethodsSubtitle")}</p>
          </div>
        )}

        {!showAddForm && paymentMethods.length > 0 && (
          <div className="payment-methods-list indented-below-h4">
            {paymentMethods.map((method) => (
              <div key={method.paymentMethodId} className="payment-method-card">
                <div className="payment-method-info">
                  <div className="payment-method-icon">
                    {getCardBrandIcon(method.cardBrand)}
                  </div>
                  <div className="payment-method-details">
                    <div className="payment-method-brand">
                      {method.cardBrand?.toUpperCase() || "Card"} ••••{" "}
                      {method.cardLast4}
                    </div>
                    <div className="payment-method-exp">
                      {t("paymentMethods.card.expires")
                        .replace("{month}", method.cardExpMonth)
                        .replace("{year}", method.cardExpYear)}
                    </div>
                    {method.cardholderName && (
                      <div className="payment-method-name">
                        {method.cardholderName}
                      </div>
                    )}
                  </div>
                </div>

                <div className="payment-method-actions">
                  {method.isDefault && (
                    <span className="badge-default">
                      {t("paymentMethods.card.default")}
                    </span>
                  )}
                  {!method.isDefault && (
                    <button
                      className="common-btn-link"
                      onClick={() => handleSetDefault(method.paymentMethodId)}
                    >
                      {t("paymentMethods.card.setDefault")}
                    </button>
                  )}
                  {!(paymentMethods.length === 1 && isRemovalBlocked()) && (
                    <button
                      className="common-btn-link text-danger"
                      onClick={() =>
                        handleDeletePaymentMethod(method.paymentMethodId)
                      }
                    >
                      {t("paymentMethods.card.remove")}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {shouldShowCompleteSubscriptionCta && (
          <div
            className="common-form-actions"
            style={{ marginTop: "16px", paddingLeft: "16px" }}
          >
            <button
              className="common-button primary"
              onClick={onCompleteSubscription}
            >
              {t("paymentMethods.activateSubscription")}
            </button>
          </div>
        )}
      </div>

      <div className="common-form-group">
        <h4>{t("paymentMethods.autoPayTitle")}</h4>
        <div className="common-form-row indented-below-h4">
          <div className="common-form-field">
            <label className="auto-pay-checkbox">
              <input type="checkbox" defaultChecked />
              <span>{t("paymentMethods.autoPayEnable")}</span>
            </label>
            <p className="form-help-text">
              {t("paymentMethods.autoPayDescription")}
            </p>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPaymentMethodToDelete(null);
        }}
        onConfirm={confirmDeletePaymentMethod}
        title={t("paymentMethods.deleteModal.title")}
        message={t("paymentMethods.deleteModal.message")}
        details={t("paymentMethods.deleteModal.details")}
        confirmText={t("paymentMethods.deleteModal.confirmText")}
        cancelText={t("paymentMethods.deleteModal.cancelText")}
        type="danger"
      />

      {onBackToSetup && (
        <div className="setup-journey-return">
          <button className="setup-journey-return-link" onClick={onBackToSetup}>
            <span className="return-arrow">←</span>
            {t("ab.setupJourney.return")}
          </button>
        </div>
      )}
    </>
  );
};

export default function PaymentMethodsWrapper({
  onBackToSetup,
  autoOpenAddForm,
  onAddFormClosed,
  onCompleteSubscription,
  currentSubscription,
}) {
  return (
    <Elements stripe={stripePromise ?? null}>
      <PaymentMethods
        onBackToSetup={onBackToSetup}
        autoOpenAddForm={autoOpenAddForm}
        onAddFormClosed={onAddFormClosed}
        onCompleteSubscription={onCompleteSubscription}
        currentSubscription={currentSubscription}
      />
    </Elements>
  );
}
